import { useMemo, useState } from 'react';
import { useMeCachedQuery } from '@frameio/shared/hooks';
import { appComponentsStorage } from '@frameio/shared/storage';
import TEXT from './generated/TEXT/usePlaceholderText';

const LAST_VIEWED_INDEX_KEY =
  'frame-io--quick-search-last-viewed-placeholder-index';

TEXT.DEFINE({
  /** Default placeholder text for mobile devices */
  defaultPlaceholderMobile: 'Search for status, assignee, and more',
  /** Default placeholder text for desktop devices */
  defaultPlaceholderDesktop: 'Search for status, assignee, keywords, and more',
  /** Prefix text for search placeholder */
  searchFor: 'Search for',
  placeholderText: {
    /** Example placeholder for footage search */
    footage: 'footage of cloudy skies',
    /** Example placeholder for comments search */
    comments: 'comments with "looks rough"',
    /** Example placeholder for transcript search */
    transcript: 'transcript containing African elephants',
    /** Example placeholder for uploads search with user name */
    uploads: ({ name }: { name: string }) => `uploads by ${name}`,
  },
});

// Array of placeholder text keys - used to determine the number of options
const PLACEHOLDER_OPTIONS_KEYS = [
  'footage',
  'comments',
  'transcript',
  'uploads',
] as const;

type UsePlaceholderTextProps = {
  isMobile: boolean;
  isSemanticSearchEnabled: boolean;
};
export const usePlaceholderText = ({
  isMobile,
  isSemanticSearchEnabled,
}: UsePlaceholderTextProps): string => {
  const [placeholderIndex] = useState(() => {
    const lastViewedIndex =
      appComponentsStorage.get(LAST_VIEWED_INDEX_KEY) ?? 0;
    const nextIndex = (lastViewedIndex + 1) % PLACEHOLDER_OPTIONS_KEYS.length;
    appComponentsStorage.set(LAST_VIEWED_INDEX_KEY, nextIndex);
    return nextIndex;
  });

  const { data: meData } = useMeCachedQuery();
  const currentUserName = meData?.me?.name ?? '';
  const currentUserFirstName = currentUserName?.split(' ')[0];

  const {
    defaultPlaceholderMobile,
    defaultPlaceholderDesktop,
    searchFor,
    placeholderText,
  } = TEXT;

  const placeholderTextUploads = placeholderText.uploads({
    name: currentUserFirstName || currentUserName,
  });

  const PlaceholderOptions = useMemo(
    () => [
      placeholderText.footage,
      placeholderText.comments,
      placeholderText.transcript,
      placeholderTextUploads,
    ],
    [placeholderText, placeholderTextUploads]
  );

  const placeholderTextValue = useMemo(() => {
    if (!isSemanticSearchEnabled) {
      return isMobile ? defaultPlaceholderMobile : defaultPlaceholderDesktop;
    }

    return `${searchFor} ${PlaceholderOptions[placeholderIndex]}`;
  }, [
    isSemanticSearchEnabled,
    isMobile,
    placeholderIndex,
    PlaceholderOptions,
    defaultPlaceholderMobile,
    defaultPlaceholderDesktop,
    searchFor,
  ]);

  return placeholderTextValue;
};
